import { useEffect, useState, type FormEvent } from 'react';
import {
  Badge,
  Box,
  Button,
  Field,
  Heading,
  HStack,
  Icon,
  Input,
  NativeSelect,
  Spinner,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { LuUserPlus, LuUsers } from 'react-icons/lu';
import { createUser, generateSetupLink, listUsers, type AdminUser } from '@/lib/api';
import { toaster } from '@/components/ui/toaster';
import { RequiredLabel, RequiredLegend } from '@/components/ui/required-field';
import { SetupLinkDialog } from '@/components/admin/SetupLinkDialog';

type FormState = {
  email: string;
  password: string;
  fullName: string;
  role: 'admin' | 'asistente';
};

const EMPTY: FormState = { email: '', password: '', fullName: '', role: 'asistente' };

const ROLE_LABEL: Record<'admin' | 'asistente', string> = {
  admin: 'Administrador',
  asistente: 'Asistente',
};

export function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setListError(null);
    try {
      setUsers(await listUsers());
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'No se pudo cargar el listado');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await createUser({ ...form, password: form.password || undefined });
      toaster.create({
        title: 'Usuario creado',
        description: `${form.fullName} (${form.email})`,
        type: 'success',
      });
      setForm(EMPTY);
      await refresh();
      if (res.setupUrl) setLinkUrl(res.setupUrl);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo crear';
      setFormError(msg);
      toaster.create({ title: 'No se pudo crear el usuario', description: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  async function onGenerateLink(userId: string) {
    try {
      const { url } = await generateSetupLink(userId);
      setLinkUrl(url);
    } catch (err) {
      toaster.create({
        title: 'No se pudo generar el enlace',
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    }
  }

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="5xl">
        <Stack gap="1">
          <Text
            fontSize="xs"
            color="fg.muted"
            textTransform="uppercase"
            letterSpacing="0.06em"
            fontWeight="700"
          >
            Administración
          </Text>
          <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
            Usuarios
          </Heading>
          <Text color="fg.muted" fontSize={{ base: 'sm', md: 'md' }}>
            Acceso por invitación. El administrador crea cada usuario manualmente.
          </Text>
        </Stack>

        <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }}>
          <HStack gap="3" mb="5">
            <Icon as={LuUserPlus} color="brand.600" boxSize="5" />
            <Heading size="md">Crear usuario</Heading>
          </HStack>
          <form onSubmit={onSubmit}>
            <Stack gap="4">
              <Stack direction={{ base: 'column', md: 'row' }} gap="4" align="flex-start">
                <Field.Root flex="1" required>
                  <RequiredLabel>Nombre completo</RequiredLabel>
                  <Input
                    value={form.fullName}
                    onChange={(e) => setForm({ ...form, fullName: e.currentTarget.value })}
                  />
                </Field.Root>
                <Field.Root flex="1" required>
                  <RequiredLabel>Correo</RequiredLabel>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
                  />
                </Field.Root>
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} gap="4" align="flex-start">
                <Field.Root flex="1">
                  <Field.Label>Contraseña (opcional)</Field.Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.currentTarget.value })}
                    minLength={12}
                    placeholder="Dejar en blanco para generar un enlace"
                  />
                </Field.Root>
                <Field.Root flex="1" required>
                  <RequiredLabel>Rol</RequiredLabel>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={form.role}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          role: e.currentTarget.value as FormState['role'],
                        })
                      }
                    >
                      <option value="asistente">Asistente</option>
                      <option value="admin">Administrador</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              </Stack>
              {formError && (
                <Text color="red.600" fontSize="sm">
                  {formError}
                </Text>
              )}
              <RequiredLegend />
              <Box>
                <Button
                  type="submit"
                  colorPalette="brand"
                  bg="brand.600"
                  color="white"
                  _hover={{ bg: 'brand.700' }}
                  loading={submitting}
                  loadingText="Creando…"
                  w={{ base: 'full', md: 'auto' }}
                >
                  Crear usuario
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>

        <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }}>
          <HStack gap="3" mb="5">
            <Icon as={LuUsers} color="brand.600" boxSize="5" />
            <Heading size="md">Listado</Heading>
          </HStack>
          {listError && (
            <Text color="red.600" fontSize="sm" mb="4">
              {listError}
            </Text>
          )}
          {loading ? (
            <HStack gap="3" color="fg.muted">
              <Spinner size="sm" />
              <Text fontSize="sm">Cargando…</Text>
            </HStack>
          ) : (
            <>
              {/* Mobile: card list */}
              <Stack gap="3" display={{ base: 'flex', md: 'none' }}>
                {users.map((u) => (
                  <Box
                    key={u.id}
                    borderWidth="1px"
                    borderColor="border"
                    rounded="md"
                    p="3"
                    bg="bg.canvas"
                  >
                    <HStack justify="space-between" align="flex-start" gap="3">
                      <Stack gap="0.5" minW={0}>
                        <Text fontWeight="600" lineHeight="1.2">
                          {u.fullName}
                        </Text>
                        <Text fontSize="sm" color="fg.muted" truncate>
                          {u.email}
                        </Text>
                      </Stack>
                      <Badge
                        colorPalette={u.role === 'admin' ? 'purple' : 'gray'}
                        flexShrink={0}
                      >
                        {ROLE_LABEL[u.role]}
                      </Badge>
                    </HStack>
                    <Button
                      size="xs"
                      variant="outline"
                      mt="2"
                      w="full"
                      onClick={() => onGenerateLink(u.id)}
                    >
                      Generar enlace
                    </Button>
                  </Box>
                ))}
              </Stack>

              {/* Desktop: table */}
              <Box display={{ base: 'none', md: 'block' }}>
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Nombre</Table.ColumnHeader>
                      <Table.ColumnHeader>Correo</Table.ColumnHeader>
                      <Table.ColumnHeader>Rol</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="end">Acciones</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {users.map((u) => (
                      <Table.Row key={u.id}>
                        <Table.Cell>{u.fullName}</Table.Cell>
                        <Table.Cell color="fg.muted">{u.email}</Table.Cell>
                        <Table.Cell>
                          <Badge colorPalette={u.role === 'admin' ? 'purple' : 'gray'}>
                            {ROLE_LABEL[u.role]}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell textAlign="end">
                          <Button size="xs" variant="outline" onClick={() => onGenerateLink(u.id)}>
                            Generar enlace
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            </>
          )}
        </Box>
      </Stack>
      <SetupLinkDialog url={linkUrl} onClose={() => setLinkUrl(null)} />
    </Box>
  );
}
